try {
            if (process.env.TOKENS_FEATURE_FLAG === 'true') require('./index-new.css');
            else require('./index.css');
          } catch (e) {
            if (e.code !== 'MODULE_NOT_FOUND') {
              throw e; // Re-throw unexpected errors
            }
            console.warn('index.css not found, proceeding without it.');
          }
          

import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Container } from '@design-system/container';
import { SearchBlock } from '@design-system/search-block';
import { Section } from '@design-system/section';
import classNames from 'classnames';
import React, { useState, useEffect, useCallback, forwardRef, useRef } from 'react';
import { Link } from '@design-system/link';
import { Button } from '@design-system/button';
import FocusTrap from 'focus-trap-react';
import { ExtendableImage } from '@design-system/extendable-image';
import { Tooltip } from '@design-system/tooltip';
import { isBrowser } from '@phx-husky/browser-helpers';
import { useScreenRecognition } from '@phx-husky/use-screen-recognition';
import { Icon } from '@design-system/icon';

const BurgerCloser = ({ burgerCloseIcon, burgerCloseLabel, isJS, }) => !isJS ? (jsx(Link, Object.assign({ className: "cds-global-header__burger-closer", variant: "subtle-icon", href: "#close-menu", size: "large", iconSuffix: {
        props: burgerCloseIcon,
    } }, { children: burgerCloseLabel }))) : null;

const BurgerOpener = ({ burgerCloseIcon, burgerOpenLabel, burgerOpenIcon, burgerToggleLabel, handleBurgerClick, isOpen, isJS, burgerBtnRef, isMobileView, }) => {
    if (isJS && isMobileView)
        return (jsx(Button, Object.assign({ ref: burgerBtnRef, variant: "subtle-icon", className: "cds-navigation-mobile__burger", size: "large", iconSuffix: {
                props: isOpen ? burgerCloseIcon : burgerOpenIcon,
            }, onClick: handleBurgerClick, "aria-expanded": !!isOpen }, { children: burgerToggleLabel })));
    if (isJS && !isMobileView) {
        return null;
    }
    return (jsx(Link, Object.assign({ className: "cds-global-header__burger-opener", variant: "subtle-icon", href: "#open-menu", size: "large", iconSuffix: {
            props: burgerOpenIcon,
        } }, { children: burgerOpenLabel })));
};

const CartIcon = ({ ariaLabel, href, LinkComponent = Link, itemsQuantity, }) => (jsx(LinkComponent, Object.assign({ className: classNames('cds-shopping-cart-icon', {
        'cds-shopping-cart-icon--no-items': itemsQuantity === 0,
    }), variant: "subtle-icon", size: "small", href: href, iconSuffix: { props: { icon: 'shoppingCart' } }, "aria-label": ariaLabel }, { children: itemsQuantity })));

const FocusTrapWrapper = ({ isOpen, isJS, isMobileView, children, }) => {
    const Wrapper = isOpen && isJS && isMobileView ? FocusTrap : React.Fragment;
    return jsx(Wrapper, { children: children });
};

const Logo = ({ className, image, link, onClick }) => {
    const LinkComponent = (link === null || link === void 0 ? void 0 : link.component) || Link;
    const linkProps = link === null || link === void 0 ? void 0 : link.props;
    return (jsx("div", Object.assign({ className: classNames('cds-logo', className) }, { children: link && linkProps ? (jsx(LinkComponent, Object.assign({ onClick: onClick }, linkProps, { className: "cds-logo__link", variant: "wrapper", tabIndex: -1, "aria-hidden": "true" }, { children: jsx(ExtendableImage, Object.assign({}, image)) }))) : (jsx(ExtendableImage, Object.assign({}, image))) })));
};

const itemClassName = 'cds-global-header__navigation-item';
const hiddenItemClassName = 'cds-global-header__navigation-item--hidden';
const moreClassName = 'cds-global-header__more-item';
const languageSwitchClassName = 'cds-global-header__lang-switch';
const logoClassName = 'cds-global-header__navigation-logo';
const searchClassName = 'cds-global-header__navigation-search';
const cartIconClassName = 'cds-global-header__cart-icon';
const profileClassName = 'cds-global-header__navigation-profile';
const navItemQuerySelector = 'li.cds-global-header__navigation-item';
const blurDelay = 100;
const keySpacebar = 'Spacebar';
const keyEnter = 'Enter';

const useBrowserScreen = () => {
    const { isMobile, isTablet, isDesktop } = useScreenRecognition();
    const isMobileView = isBrowser() && isMobile;
    const isTabletView = isBrowser() && isTablet;
    const isDesktopView = isBrowser() && isDesktop;
    return { isMobileView, isTabletView, isDesktopView };
};

const useJS = () => {
    const [isJS, setIsJS] = useState(false);
    useEffect(() => {
        setIsJS(!!typeof window);
    }, []);
    return [isJS];
};

const useDebounceFunc = (func, milliseconds = 400) => {
    const time = milliseconds;
    let timer;
    return (event) => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(func, time, event);
    };
};

const useNavigationResize = ({ listRef, navRef, navigationData, }) => {
    const { isMobileView } = useBrowserScreen();
    const [visibleItems, setVisibleItems] = useState(navigationData);
    const [hiddenItems, setHiddenItems] = useState([]);
    const [widthData, setWidthData] = useState(null);
    const [logoWidth, setLogoWidth] = useState(0);
    const [searchWidth, setSearchWidth] = useState(0);
    const [cartIconWidth, setCartIconWidth] = useState(0);
    const [profileWidth, setProfileWidth] = useState(0);
    const handleResize = useCallback(() => {
        if (widthData && navRef.current) {
            const navElWidth = navRef.current.offsetWidth;
            const { navItems, langSwitchBtnWidth } = widthData;
            const moreItemElement = navRef.current.getElementsByClassName(moreClassName)[0];
            const moreBtnWidth = (moreItemElement === null || moreItemElement === void 0 ? void 0 : moreItemElement.clientWidth) || 0;
            let sum = langSwitchBtnWidth +
                moreBtnWidth +
                logoWidth +
                searchWidth +
                cartIconWidth +
                profileWidth;
            const visibleArr = [];
            const hiddenArr = [];
            navItems.forEach((_, index) => {
                const navItem = document.querySelectorAll(navItemQuerySelector)[index];
                const navItemWidth = navItem && navItem.getBoundingClientRect().width;
                sum += navItemWidth || 0;
                if (navElWidth >= sum) {
                    visibleArr.push(navigationData[index]);
                }
                else {
                    hiddenArr.push(navigationData[index]);
                }
            });
            setVisibleItems(visibleArr);
            setHiddenItems(hiddenArr);
        }
    }, [
        searchWidth,
        logoWidth,
        navRef,
        navigationData,
        widthData,
        cartIconWidth,
        profileWidth,
    ]);
    useEffect(() => {
        if ((listRef === null || listRef === void 0 ? void 0 : listRef.current) && !isMobileView) {
            const widthDataObj = { navItems: [], langSwitchBtnWidth: 0 };
            const children = Array.from(listRef.current.children);
            const visibleItemsCount = children.filter(({ className }) => className.includes(itemClassName) &&
                !className.includes(moreClassName) &&
                !className.includes(languageSwitchClassName)).length;
            if (visibleItemsCount !== navigationData.length)
                return;
            children.forEach((item) => {
                if (item.className.includes(itemClassName) &&
                    !item.className.includes(moreClassName) &&
                    !item.className.includes(languageSwitchClassName)) {
                    widthDataObj.navItems.push({ width: item.clientWidth, item });
                }
                if (item.className.includes(languageSwitchClassName)) {
                    widthDataObj.langSwitchBtnWidth = item.clientWidth;
                }
            });
            setWidthData(widthDataObj);
        }
    }, [listRef, navigationData, isMobileView]);
    useEffect(() => {
        handleResize();
    }, [handleResize, isMobileView]);
    const debouncedHandleResize = useDebounceFunc(handleResize, 400);
    useEffect(() => {
        if (!isMobileView) {
            window.addEventListener('resize', debouncedHandleResize);
        }
        else {
            window.removeEventListener('resize', debouncedHandleResize);
        }
        return () => {
            window.removeEventListener('resize', debouncedHandleResize);
        };
    }, [debouncedHandleResize, isMobileView]);
    useEffect(() => {
        let RO;
        if (listRef.current && !isMobileView) {
            RO = new ResizeObserver((entries) => {
                const targetArr = Array.from(entries[0].target.children);
                const itemsWithoutLogo = targetArr.filter((el) => !el.className.includes(logoClassName));
                const logo = targetArr.find((el) => el.className.includes(logoClassName));
                const search = targetArr.find((el) => el.className.includes(searchClassName));
                const cartIcon = targetArr.find((el) => el.className.includes(cartIconClassName));
                const profile = targetArr.find((el) => el.className.includes(profileClassName));
                itemsWithoutLogo.forEach((menuElement) => menuElement.classList.add(hiddenItemClassName));
                if (logo === null || logo === void 0 ? void 0 : logo.clientWidth) {
                    const logoComputedStyle = getComputedStyle(logo);
                    const logoPaddingRight = parseFloat(logoComputedStyle.paddingRight);
                    const navElComputedStyle = getComputedStyle(itemsWithoutLogo[0]);
                    const navElPaddingLeft = parseFloat(navElComputedStyle.paddingLeft);
                    setLogoWidth(logo.clientWidth + navElPaddingLeft + logoPaddingRight);
                }
                if (search === null || search === void 0 ? void 0 : search.clientWidth) {
                    setSearchWidth(search.clientWidth);
                }
                if (cartIcon === null || cartIcon === void 0 ? void 0 : cartIcon.clientWidth) {
                    setCartIconWidth(cartIcon.clientWidth);
                }
                if (profile === null || profile === void 0 ? void 0 : profile.clientWidth) {
                    setProfileWidth(profile.clientWidth);
                }
                itemsWithoutLogo.forEach((menuElement) => menuElement.classList.remove(hiddenItemClassName));
                handleResize();
            });
            RO.observe(listRef.current);
            return () => {
                RO.disconnect();
            };
        }
        if (listRef.current && isMobileView && RO) {
            RO.disconnect();
        }
        return undefined;
    }, [listRef, isMobileView, handleResize]);
    return { visibleItems, hiddenItems };
};

const shouldBeRendered = (data, conditions, operator = 'or') => {
    if (!data)
        return false;
    if (operator === 'or') {
        return conditions.some((c) => c);
    }
    if (operator === 'and') {
        return conditions.every((c) => c);
    }
    return false;
};

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function __rest$1(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

try {
  if (process.env.TOKENS_FEATURE_FLAG === 'true') require('./index-new.css');else require('./index.css');
} catch (e) {
  if (e.code !== 'MODULE_NOT_FOUND') {
    throw e; // Re-throw unexpected errors
  }

  console.warn('index.css not found, proceeding without it.');
}
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

var getClassName = function getClassName(_ref) {
  var _classNames;

  var align = _ref.align,
      variant = _ref.variant,
      wrapping = _ref.wrapping,
      className = _ref.className,
      size = _ref.size,
      weight = _ref.weight;
  var isBodyOrHeadlineOrDetail = variant === 'body' || variant === 'headline' || variant === 'detail';
  return classNames('cds-typography', className, (_classNames = {}, _defineProperty(_classNames, "cds-typography--align-".concat(align), align), _defineProperty(_classNames, "cds-typography--variant-".concat(variant), variant), _defineProperty(_classNames, "cds-typography--variant-".concat(variant, "-").concat(size), isBodyOrHeadlineOrDetail && size), _defineProperty(_classNames, "cds-typography--wrapping-".concat(wrapping), wrapping), _defineProperty(_classNames, "cds-typography--weight-".concat(weight), isBodyOrHeadlineOrDetail && weight), _classNames));
};

var unwrapFirstTag = function unwrapFirstTag(typographyTag, typographyVariant, typographyContent) {
  var openTagRegExp = /^<\/?[^>]+(>|$)/g;
  var closeTagRegExp = /<\/[^>]+(>$)/g;
  return typographyVariant !== 'rte' && typographyTag !== 'div' ? typographyContent.toString().replace(openTagRegExp, '').replace(closeTagRegExp, '') : typographyContent;
};

var Typography = /*#__PURE__*/forwardRef(function (_a, ref) {
  var content = _a.content,
      _a$tag = _a.tag,
      tag = _a$tag === void 0 ? 'div' : _a$tag,
      variant = _a.variant,
      align = _a.align,
      className = _a.className,
      _a$size = _a.size,
      size = _a$size === void 0 ? 'm' : _a$size,
      _a$weight = _a.weight,
      weight = _a$weight === void 0 ? 'regular' : _a$weight,
      _a$wrapping = _a.wrapping,
      wrapping = _a$wrapping === void 0 ? 'break-word' : _a$wrapping,
      link = _a.link,
      htmlProperties = __rest(_a, ["content", "tag", "variant", "align", "className", "size", "weight", "wrapping", "link"]);

  var LinkElement = (link === null || link === void 0 ? void 0 : link.component) || Link;
  var typographyClassName = getClassName({
    align: align,
    variant: variant,
    wrapping: wrapping,
    className: className,
    size: size,
    weight: weight
  });
  var Tag = tag && variant !== 'rte' ? tag : 'div';
  var html = unwrapFirstTag(tag, variant, content);
  return link ? jsx(Tag, Object.assign({
    className: typographyClassName,
    ref: ref
  }, htmlProperties, {
    children: jsx(LinkElement, Object.assign({}, link.props, {
      iconPrefix: undefined,
      iconSuffix: undefined
    }, {
      children: jsx("span", {
        dangerouslySetInnerHTML: {
          __html: html
        }
      })
    }))
  })) : jsx(Tag, Object.assign({
    className: typographyClassName,
    ref: ref,
    dangerouslySetInnerHTML: {
      __html: html
    }
  }, htmlProperties));
});

const useDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const timer = useRef();
    const { isMobileView } = useBrowserScreen();
    const handleShowMoreClick = (e) => {
        e.preventDefault();
        setIsOpen((prevVal) => !prevVal);
    };
    const handleMenuItemClick = (e, hasSubitems, handleMobileNavigationItemClick) => {
        if (!isMobileView) {
            return;
        }
        if (hasSubitems) {
            e.preventDefault();
            setIsOpen((prevVal) => !prevVal);
        }
        else {
            handleMobileNavigationItemClick();
        }
    };
    const handleMouseOver = (e) => {
        e.preventDefault();
        if (!isOpen) {
            setIsOpen(true);
        }
    };
    const handleKeyPress = (e) => {
        if (e.key === ' ' || e.key === keySpacebar || e.key === keyEnter) {
            e.preventDefault();
            setIsOpen((prevVal) => !prevVal);
        }
    };
    const handleDesktopBlur = () => {
        if (isMobileView) {
            return;
        }
        timer.current = setTimeout(() => {
            setIsOpen(false);
        }, blurDelay);
    };
    const handleDesktopOutsideBlur = (e) => {
        if (e.currentTarget.contains(e.relatedTarget) && !isMobileView) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        handleDesktopBlur();
    };
    const handleFocus = () => {
        if (timer.current)
            clearTimeout(timer.current);
    };
    const handleEsc = (event) => {
        if (event.key === 'Escape') {
            if (timer.current)
                clearTimeout(timer.current);
            setIsOpen(false);
        }
    };
    useEffect(() => {
        window.addEventListener('keyup', handleEsc);
        return () => {
            window.removeEventListener('keyup', handleEsc);
        };
    }, []);
    return {
        handleDesktopBlur,
        handleDesktopOutsideBlur,
        handleFocus,
        handleShowMoreClick,
        handleMenuItemClick,
        handleKeyPress,
        handleMouseOver,
        isOpen,
    };
};

const getLinkIcon = (openIcon, closeIcon, isOpen, shouldShow = true) => shouldShow && openIcon && closeIcon
    ? {
        props: isOpen ? closeIcon : openIcon,
    }
    : undefined;

const getLinkSize = (smallFactor) => smallFactor ? 'large' : 'small';

const LanguageSwitch = ({ title, ariaLabel, languageData, linkNavElement: LinkElement = Link, openIcon, closeIcon, isMobileView, }) => {
    const { handleDesktopBlur, handleDesktopOutsideBlur, handleFocus, handleShowMoreClick, handleKeyPress, isOpen, handleMouseOver, } = useDropdown();
    const onMouseOverDesktop = !isMobileView ? handleMouseOver : undefined;
    return (jsxs("li", Object.assign({ className: classNames('cds-global-header__navigation-item', 'cds-global-header__navigation-item--has-children', languageSwitchClassName), onBlur: handleDesktopOutsideBlur, onMouseLeave: handleDesktopBlur }, { children: [jsx(Button, Object.assign({ onKeyDown: handleKeyPress, onClick: handleShowMoreClick, onBlur: handleDesktopBlur, onMouseOver: onMouseOverDesktop, variant: "nav", size: getLinkSize(isMobileView), "aria-label": ariaLabel, "aria-expanded": isOpen, className: classNames('cds-global-header__lang-switch-btn', {
                    active: isOpen,
                }), iconPrefix: {
                    props: {
                        icon: 'translation',
                        className: 'cds-global-header__lang-switch-icon',
                    },
                }, iconSuffix: getLinkIcon(openIcon, closeIcon, isOpen, !isMobileView) }, { children: title })), isMobileView ? (jsx(Button, Object.assign({ variant: "nav", onClick: handleShowMoreClick, onKeyDown: handleKeyPress, size: getLinkSize(isMobileView), className: "cds-global-header__navigation-arrow", "aria-expanded": isOpen, iconSuffix: getLinkIcon(openIcon, closeIcon, isOpen) }, { children: title }))) : null, jsx("div", Object.assign({ className: "cds-global-header__navigation-drop" }, { children: jsx("ul", Object.assign({ className: "cds-global-header__lang-list" }, { children: languageData.map((_a) => {
                        var { label, href, locale, translated } = _a, item = __rest$1(_a, ["label", "href", "locale", "translated"]);
                        return (jsx("li", { children: translated ? (jsx(LinkElement, Object.assign({ className: "cds-global-header__lang-link" }, item, { href: href, onClick: handleDesktopBlur, variant: "nav", size: getLinkSize(isMobileView), onFocus: handleFocus, onBlur: handleDesktopBlur }, { children: label }))) : (jsx(Typography, { className: "cds-global-header__lang-item", content: label, tag: "span", variant: "body" })) }, locale));
                    }) })) }))] })));
};

const isActive = (activePath, linkHref, subLinks) => subLinks.some((el) => activePath.includes(el.href)) ||
    activePath === linkHref;

const SubNavigation = ({ className, subItems, linkNavElement: LinkElement = Link, subMenuLabel, activeUrlPath, handleFocus, handleBlur, isMobileView, href, ariaLabel, handleMobileNavigationItemClick, onClickSubnavItem, }) => {
    const handleClick = (subLabel) => {
        if (onClickSubnavItem)
            onClickSubnavItem(subLabel);
        if (isMobileView)
            handleMobileNavigationItemClick();
    };
    return (jsx("div", Object.assign({ className: classNames('cds-global-header__navigation-drop', className) }, { children: jsxs("ul", Object.assign({ className: "cds-global-header__sub-list" }, { children: [shouldBeRendered(subMenuLabel, [isMobileView]) && (jsx("li", Object.assign({ className: "cds-navigation__sub-item" }, { children: jsx(LinkElement, Object.assign({ className: classNames('cds-navigation__sub-link', {
                            active: activeUrlPath === href,
                        }), "aria-current": activeUrlPath === href, "aria-label": ariaLabel, onClick: handleMobileNavigationItemClick, href: href, variant: "nav", size: "large", onFocus: handleFocus, onBlur: handleBlur }, { children: subMenuLabel })) }))), subItems.map((_a) => {
                    var { label: subLabel, href: subHref, ariaLabel: subAriaLabel, subMenuLabel: _subMenuLabel, subItems: _subItems } = _a, subItem = __rest$1(_a, ["label", "href", "ariaLabel", "subMenuLabel", "subItems"]);
                    return (jsx("li", { children: jsx(LinkElement, Object.assign({ className: classNames('cds-global-header__sub-link', {
                                active: activeUrlPath === subHref,
                            }), "aria-current": activeUrlPath === subHref }, subItem, { "aria-label": subAriaLabel, onClick: () => handleClick(subLabel), href: subHref, variant: "nav", size: getLinkSize(isMobileView), onFocus: handleFocus, onBlur: handleBlur }, { children: subLabel })) }, subLabel));
                })] })) })));
};

const NavigationItem = ({ className, linkNavElement, activeUrlPath, href = '', ariaLabel, isMobileView, label, subItems, subMenuLabel, openIcon, closeIcon, expandTitle, onClickNavItem, handleMobileNavigationItemClick, }) => {
    const LinkElement = linkNavElement || Link;
    const { handleDesktopBlur, handleDesktopOutsideBlur, handleFocus, handleMenuItemClick, handleKeyPress, isOpen, handleMouseOver, } = useDropdown();
    const onMouseOverDesktop = subItems && !isMobileView ? handleMouseOver : undefined;
    const handleClick = (e) => {
        if (onClickNavItem)
            onClickNavItem(label);
        handleMenuItemClick(e, Boolean(subItems === null || subItems === void 0 ? void 0 : subItems.length), handleMobileNavigationItemClick);
    };
    return (jsxs("li", Object.assign({ className: classNames('cds-global-header__navigation-item', className, {
            'cds-global-header__navigation-item--has-children': Boolean(subItems),
            active: isActive(activeUrlPath, href, subItems || []),
        }), onBlur: handleDesktopOutsideBlur, onMouseLeave: handleDesktopBlur }, { children: [jsx(LinkElement, Object.assign({ className: classNames('cds-global-header__navigation-link', {
                    active: isActive(decodeURIComponent(activeUrlPath), href, subItems || []),
                    'has-submenu': Boolean(subItems === null || subItems === void 0 ? void 0 : subItems.length),
                }), onClick: handleClick, "aria-current": decodeURIComponent(activeUrlPath) === href, onMouseOver: onMouseOverDesktop, href: href, variant: "nav", size: getLinkSize(isMobileView), "aria-label": ariaLabel }, { children: label })), Boolean(subItems === null || subItems === void 0 ? void 0 : subItems.length) && (jsx(Button, Object.assign({ variant: "nav", onClick: handleClick, onKeyDown: handleKeyPress, onMouseOver: onMouseOverDesktop, size: getLinkSize(isMobileView), className: "cds-global-header__navigation-arrow", "aria-expanded": isOpen, iconSuffix: getLinkIcon(openIcon, closeIcon, isOpen) }, { children: `${expandTitle} ${label}` }))), Boolean(subItems === null || subItems === void 0 ? void 0 : subItems.length) && (jsx(SubNavigation, { subItems: subItems, activeUrlPath: decodeURIComponent(activeUrlPath), subMenuLabel: subMenuLabel, linkNavElement: linkNavElement, handleFocus: handleFocus, handleBlur: handleDesktopBlur, isMobileView: isMobileView, href: href, ariaLabel: ariaLabel, handleMobileNavigationItemClick: handleMobileNavigationItemClick, onClickSubnavItem: onClickNavItem }))] })));
};

const Profile = ({ headline, greeting, icon: { icon }, account, orders, savedProducts, register, logIn, logOut, isSignedIn, handleSignOut, handleLogIn, handleRegister, userName, linkNavElement: LinkElement = Link, isMobileView, }) => (jsx("div", Object.assign({ className: "cds-global-header-profile" }, { children: isSignedIn ? (jsxs(Fragment, { children: [jsx(Typography, { className: "cds-global-header-profile__greeting", variant: "body", weight: "medium", size: "m", tag: "p", content: `${greeting}${userName !== null && userName !== void 0 ? userName : ''} 👋` }), jsx(LinkElement, Object.assign({ className: "cds-global-header-profile__link" }, orders)), jsx(LinkElement, Object.assign({ className: "cds-global-header-profile__link" }, savedProducts)), jsx(LinkElement, Object.assign({ className: "cds-global-header-profile__link" }, account)), jsx(Button, Object.assign({ className: "cds-global-header-profile__button cds-global-header-profile__button--log-out" }, logOut, { variant: isMobileView ? 'primary' : 'secondary', onClick: handleSignOut }))] })) : (jsxs(Fragment, { children: [jsxs("div", Object.assign({ className: "cds-global-header-profile__headline-wrapper" }, { children: [isMobileView ? (jsx(Icon, { className: "cds-global-header-profile__headline-icon", icon: icon })) : null, jsx(Typography, { className: "cds-global-header-profile__headline", variant: "body", weight: "medium", size: "m", tag: "p", content: headline })] })), jsxs("div", Object.assign({ className: "cds-global-header-profile__buttons-wrapper" }, { children: [jsx(Button, Object.assign({ className: "cds-global-header-profile__button cds-global-header-profile__button--register" }, register, { onClick: handleRegister })), jsx(Button, Object.assign({ className: "cds-global-header-profile__button cds-global-header-profile__button--log-in" }, logIn, { onClick: handleLogIn }))] }))] })) })));

const Navigation = ({ navigationName, navigationData, linkNavElement, onClickNavItem, className, closeIcon, openIcon, activeUrlPath, logo, moreBtn, handleMobileNavigationItemClick, languageSwitch, search, cartIcon, profile, }) => {
    const listRef = useRef(null);
    const navRef = useRef(null);
    const { isMobileView } = useBrowserScreen();
    const [isJS] = useJS();
    const { visibleItems, hiddenItems } = useNavigationResize({
        navigationData,
        listRef,
        navRef,
    });
    return (jsx("nav", Object.assign({ ref: navRef, className: classNames('cds-global-header__navigation', className), "aria-label": navigationName }, { children: jsxs("ul", Object.assign({ ref: listRef, className: "cds-global-header__navigation-list" }, { children: [logo && (jsx("li", Object.assign({ className: "cds-global-header__navigation-logo" }, { children: jsx(Logo, { className: "cds-global-header__desktop-logo", image: logo.imageLarge, link: logo.link }) }))), visibleItems.map((item) => (jsx(NavigationItem, Object.assign({ linkNavElement: linkNavElement, activeUrlPath: activeUrlPath, isMobileView: isMobileView, openIcon: openIcon, closeIcon: closeIcon, expandTitle: moreBtn.title, isJS: isJS, onClickNavItem: onClickNavItem, handleMobileNavigationItemClick: handleMobileNavigationItemClick }, item), item.label))), shouldBeRendered(moreBtn, [isJS, !isMobileView, (hiddenItems === null || hiddenItems === void 0 ? void 0 : hiddenItems.length) > 0], 'and') ? (jsx(NavigationItem, { className: classNames(moreClassName, {
                        [`${moreClassName}--hidden`]: !navigationData.length,
                    }), linkNavElement: linkNavElement, activeUrlPath: activeUrlPath, isMobileView: isMobileView, openIcon: openIcon, closeIcon: closeIcon, expandTitle: moreBtn.title, subItems: hiddenItems, ariaLabel: moreBtn === null || moreBtn === void 0 ? void 0 : moreBtn.ariaLabel, handleMobileNavigationItemClick: handleMobileNavigationItemClick, isJS: isJS, label: moreBtn.title, onClickNavItem: onClickNavItem, href: "/#more" })) : null, languageSwitch ? (jsx(LanguageSwitch, Object.assign({ linkNavElement: linkNavElement, openIcon: openIcon, closeIcon: closeIcon, isMobileView: isMobileView }, languageSwitch))) : null, search && !isMobileView ? (jsx("li", Object.assign({ className: "cds-global-header__navigation-search\n                        cds-global-header__navigation-icon" }, { children: jsx(SearchBlock, Object.assign({}, search, { handleBurgerMenu: handleMobileNavigationItemClick })) }))) : null, cartIcon && !isMobileView ? (jsx("li", Object.assign({ className: classNames(cartIconClassName, 'cds-global-header__navigation-icon') }, { children: jsx(CartIcon, Object.assign({}, cartIcon)) }))) : null, profile ? (jsx("li", Object.assign({ className: classNames(profileClassName, {
                        'cds-global-header__navigation-icon': !isMobileView,
                    }) }, { children: !isMobileView ? (jsx(Tooltip, { tooltipWindowClassName: "cds-global-header-profile-window", portalQuerySelector: ".cds-tooltip", label: profile.headline, isTooltipContentInteractive: true, icon: "profile", content: jsx(Profile, Object.assign({}, profile)) })) : (jsx(Profile, Object.assign({}, profile, { isMobileView: isMobileView }))) }))) : null] })) })));
};

const SkipLink = (_a) => {
    var { children, mode } = _a, linkProps = __rest$1(_a, ["children", "mode"]);
    return (jsx(Section, Object.assign({ element: "div", mode: mode, className: "cds-skip-link" }, { children: jsx(Link, Object.assign({ className: "cds-skip-link__link" }, linkProps, { children: children })) })));
};

const UtilityLinks = ({ linkNavElement: LinkElement = Link, utilityLinksData = [], utilityLinksName, }) => (jsx("nav", Object.assign({ "aria-label": utilityLinksName, className: "cds-utility-navigation" }, { children: jsx("ul", Object.assign({ className: "cds-utility-navigation__list" }, { children: utilityLinksData.map((_a) => {
            var { href, label, ariaLabel } = _a, rest = __rest$1(_a, ["href", "label", "ariaLabel"]);
            return (jsx("li", Object.assign({ className: "cds-utility-navigation__item" }, { children: jsx(LinkElement, Object.assign({ className: "cds-utility-navigation__link", href: href, variant: "subtle", size: "small", "aria-label": ariaLabel }, rest, { children: label })) }), label));
        }) })) })));

const useBurgerMenu = (headerReference) => {
    const DOCUMENT_LOCK_CLASSNAME = 'cds-scroll-locked';
    const { isMobileView } = useBrowserScreen();
    const [isOpen, setIsOpen] = useState(false);
    const [burgerOffsetTop, setBurgerOffsetTop] = useState(0);
    const burgerBtnRef = useRef(null);
    const handleBurgerClick = (e) => {
        e.preventDefault();
        const headerEl = headerReference.current;
        if (headerEl) {
            setBurgerOffsetTop(headerEl.getBoundingClientRect().top + headerEl.offsetHeight);
        }
        setIsOpen(!isOpen);
    };
    const handleMobileNavigationItemClick = () => {
        setIsOpen(!isOpen);
    };
    const handleMobileLogoClick = () => {
        setIsOpen(false);
    };
    const handler = useCallback(() => {
        if (!isMobileView) {
            setIsOpen(false);
        }
    }, [isMobileView]);
    useEffect(() => {
        setIsOpen(false);
    }, []);
    useEffect(() => {
        if (isOpen) {
            document.documentElement.classList.add(DOCUMENT_LOCK_CLASSNAME);
        }
        else {
            document.documentElement.classList.remove(DOCUMENT_LOCK_CLASSNAME);
        }
        if (burgerBtnRef.current) {
            burgerBtnRef.current.focus();
        }
    }, [isOpen]);
    useEffect(() => {
        window.addEventListener('resize', handler);
        window.addEventListener('orientationchange', handler);
        return () => {
            window.removeEventListener('resize', handler);
            window.removeEventListener('orientationchange', handler);
        };
    }, [handler]);
    return {
        isOpen,
        burgerBtnRef,
        handleBurgerClick,
        burgerOffsetTop,
        handleMobileNavigationItemClick,
        handleMobileLogoClick,
    };
};

const useStickyObserver = (reference, callback) => {
    const [isSticky, setSticky] = useState(false);
    useEffect(() => {
        const stickyElm = reference.current;
        if (stickyElm && window) {
            const observer = new IntersectionObserver(([e]) => {
                setSticky(e.intersectionRatio < 1);
                if (e.intersectionRatio === 1 && callback) {
                    callback(stickyElm === null || stickyElm === void 0 ? void 0 : stickyElm.dataset.target);
                }
            }, { threshold: [1, 0] });
            observer.observe(stickyElm);
            return () => {
                observer.unobserve(stickyElm);
            };
        }
        return undefined;
    }, [reference, callback]);
    return [isSticky];
};

const GlobalHeader = ({ className, logo, isHeaderSticky = false, skipLink, utilityLinksData, utilityLinksName, mode, linkNavElement, onClickNavItem, burgerToggleLabel, burgerOpenLabel, burgerCloseLabel, burgerOpenIcon, burgerCloseIcon, navigationName, navigationData, activeUrlPath, moreBtn, openIcon, closeIcon, languageSwitch, search, cartIcon, profile, renderOnStart, renderOnEnd, renderOnBurgerPopupStart, renderOnBurgerPopupEnd, uid, }) => {
    const headerRef = useRef(null);
    const [isSticky] = useStickyObserver(headerRef);
    const [isJS] = useJS();
    const { isOpen, burgerBtnRef, handleBurgerClick, burgerOffsetTop, handleMobileNavigationItemClick, handleMobileLogoClick, } = useBurgerMenu(headerRef);
    const { isMobileView } = useBrowserScreen();
    useEffect(() => {
        const htmlObject = document.getElementsByTagName('html')[0];
        if (isHeaderSticky) {
            htmlObject.classList.add('has-sticky-nav');
        }
        else {
            htmlObject.classList.remove('has-sticky-nav');
        }
    }, [isHeaderSticky]);
    return (jsxs(Section, Object.assign({ ref: headerRef, element: "header", id: "open-menu", mode: mode, removeDuplicateSpacing: false, "data-status": isJS ? 'js' : 'no-js', className: classNames('cds-global-header', className, {
            [`cds-global-header--is-sticky`]: isSticky && isHeaderSticky,
            [`cds-global-header--sticky-mode`]: isHeaderSticky,
            [`cds-global-header--burger-opened`]: isMobileView && isOpen,
        }), "data-component-uid": uid }, { children: [jsx(SkipLink, Object.assign({}, skipLink)), jsx(Container, Object.assign({ maxWidth: "max-grid" }, { children: jsx(FocusTrapWrapper, Object.assign({ isOpen: isOpen, isMobileView: isMobileView, isJS: isJS }, { children: jsxs("div", Object.assign({ className: "header-portal" }, { children: [renderOnStart, jsxs("div", Object.assign({ className: "cds-global-header__mobile-main" }, { children: [logo && (jsx(Logo, { className: "cds-global-header__mobile-logo", image: logo.imageSmall, link: logo.link, onClick: handleMobileLogoClick })), isMobileView && (jsxs("ul", Object.assign({ className: "cds-global-header__navigation-wrapper" }, { children: [search ? (jsx("li", Object.assign({ className: classNames('cds-global-header__navigation-search', 'cds-global-header__navigation-icon') }, { children: jsx(SearchBlock, Object.assign({}, search, { handleBurgerMenu: handleMobileNavigationItemClick })) }))) : null, cartIcon ? (jsx("li", Object.assign({ className: classNames(cartIconClassName, 'cds-global-header__navigation-icon') }, { children: jsx(CartIcon, Object.assign({}, cartIcon)) }))) : null, jsx("li", { children: jsx(BurgerOpener, { burgerToggleLabel: burgerToggleLabel, burgerOpenIcon: burgerOpenIcon, burgerCloseIcon: burgerCloseIcon, burgerOpenLabel: burgerOpenLabel, isJS: isJS, handleBurgerClick: handleBurgerClick, isOpen: isOpen, burgerBtnRef: burgerBtnRef, isMobileView: isMobileView }) })] })))] })), isOpen || !isMobileView || !isJS ? (jsxs("div", Object.assign({ style: { top: `${burgerOffsetTop}px` }, className: "cds-global-header__burger-popup" }, { children: [renderOnBurgerPopupStart, jsx(BurgerCloser, { burgerCloseIcon: burgerCloseIcon, burgerCloseLabel: burgerCloseLabel, isJS: isJS }), shouldBeRendered(utilityLinksData, [!isJS, !isMobileView], 'or') && (jsx("div", Object.assign({ className: "cds-global-header__top" }, { children: jsx(UtilityLinks, { utilityLinksData: utilityLinksData, linkNavElement: linkNavElement, utilityLinksName: utilityLinksName }) }))), jsx("div", Object.assign({ className: "cds-global-header__desktop-main" }, { children: jsx(Navigation, { navigationName: navigationName, navigationData: navigationData, activeUrlPath: activeUrlPath, linkNavElement: linkNavElement, handleMobileNavigationItemClick: handleMobileNavigationItemClick, logo: logo, moreBtn: moreBtn, openIcon: openIcon, closeIcon: closeIcon, languageSwitch: languageSwitch, search: search, cartIcon: cartIcon, onClickNavItem: onClickNavItem, profile: profile }) })), shouldBeRendered(utilityLinksData, [isJS, isMobileView], 'and') && (jsx("div", Object.assign({ className: "cds-global-header__bottom" }, { children: jsx(UtilityLinks, { utilityLinksData: utilityLinksData, linkNavElement: linkNavElement, utilityLinksName: utilityLinksName }) }))), renderOnBurgerPopupEnd] }))) : null, renderOnEnd] })) })) }))] })));
};

export { GlobalHeader };
